import React, { useEffect, useState } from 'react';
import { pagamentosApi, Assinante } from '../api/api';

export default function AssinantesPage() {
  const [assinantes, setAssinantes] = useState<Assinante[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    pagamentosApi.listarAssinantes()
      .then(r => setAssinantes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtrados = assinantes.filter(a =>
    a.nomeCompleto.toLowerCase().includes(busca.toLowerCase()) ||
    a.email.toLowerCase().includes(busca.toLowerCase())
  );

  const ativos = assinantes.filter(a => a.assinaturaExpiracao && new Date(a.assinaturaExpiracao) > new Date());
  const emTeste = assinantes.filter(a => a.modoTeste && a.assinaturaExpiracao && new Date(a.assinaturaExpiracao) > new Date());
  const expirados = assinantes.filter(a => !a.assinaturaExpiracao || new Date(a.assinaturaExpiracao) <= new Date());

  function formatarExpiracao(expiracao: string | null): string {
    if (!expiracao) return '—';
    return new Date(expiracao).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function badgeStatus(a: Assinante) {
    const ativo = a.assinaturaExpiracao && new Date(a.assinaturaExpiracao) > new Date();
    if (!ativo) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">Expirado</span>;
    }
    if (a.modoTeste) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">Teste grátis</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">Ativo</span>;
  }

  function diasRestantesLabel(a: Assinante) {
    if (!a.assinaturaExpiracao) return '—';
    const diff = new Date(a.assinaturaExpiracao).getTime() - new Date().getTime();
    if (diff <= 0) return 'Expirado';
    const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (dias === 1) return '1 dia';
    return `${dias} dias`;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Assinantes</h1>
        <p className="text-sm text-gray-500 mt-1">Gerencie todos os assinantes da plataforma</p>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border p-5 bg-white border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Assinaturas ativas</p>
          <p className="text-3xl font-bold text-emerald-700 mt-1">{ativos.length}</p>
        </div>
        <div className="rounded-xl border p-5 bg-white border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Em teste gratuito</p>
          <p className="text-3xl font-bold text-yellow-500 mt-1">{emTeste.length}</p>
        </div>
        <div className="rounded-xl border p-5 bg-white border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Expirados</p>
          <p className="text-3xl font-bold text-red-500 mt-1">{expirados.length}</p>
        </div>
      </div>

      {/* Busca */}
      <div className="rounded-xl border p-4 bg-white border border-gray-200">
        <input
          type="text"
          placeholder="Buscar por nome ou e-mail..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Tabela */}
      <div className="rounded-xl border overflow-hidden bg-white border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Carregando...</div>
        ) : filtrados.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Nenhum assinante encontrado</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuário</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Expira em</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Dias restantes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtrados.map(a => (
                <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-gray-900">{a.nomeCompleto}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{a.email}</p>
                  </td>
                  <td className="px-5 py-3.5">{badgeStatus(a)}</td>
                  <td className="px-5 py-3.5 text-gray-600">{formatarExpiracao(a.assinaturaExpiracao)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`font-medium ${
                      !a.assinaturaExpiracao || new Date(a.assinaturaExpiracao) <= new Date()
                        ? 'text-red-500'
                        : (a.diasRestantes ?? 0) <= 3
                          ? 'text-orange-500'
                          : 'text-gray-700'
                    }`}>
                      {diasRestantesLabel(a)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
